import React, { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import FocusRing from '@atlaskit/focus-ring';
import mergeRefs from '../../../../utils/merge-refs.tsx';
import useLazyCallback from '../../../../utils/use-lazy-callback.tsx';
import useStateRef from '../../../../utils/use-state-ref.tsx';
import { keylineHeight, keylineColor } from './constants.tsx';
import { rafSchedule } from './utils.tsx';

// Based on atlassian-frontend/packages/design-system/modal-dialog/src/internal/components/scroll-container.tsx
type ScrollContainerProps = {
	/**
	 * Children of the body within modal dialog.
	 */
	children: ReactNode;
	/**
	 * A `testId` prop is provided for specified elements,
	 * which is a unique string that appears as a data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Allow user to disable showing the keylines even when scrolling.
	 */
	disableAutoTopKeyline?: boolean;
	disableAutoBottomKeyline?: boolean;
};

/**
 * A container that shows top and bottom keylines when the
 * content overflows into the scrollable element.
 */
const ScrollContainer = forwardRef<HTMLDivElement, ScrollContainerProps>(
	(props: ScrollContainerProps, ref) => {
		const {
			testId,
			children,
			disableAutoTopKeyline = true,
			disableAutoBottomKeyline = true,
		} = props;
		const [hasSiblings, setSiblings] = useStateRef({
			previous: false,
			next: false,
		});

		const [showContentFocus, setContentFocus] = useState(false);
		const [showTopKeyline, setTopKeyline] = useState(false);
		const [showBottomKeyline, setBottomKeyline] = useState(false);

		const scrollableRef = useRef<HTMLDivElement>(null);

		const setLazySiblings = useLazyCallback(setSiblings);

		const setLazyContentFocus = useLazyCallback(
			rafSchedule(() => {
				const target = scrollableRef.current;
				target && setContentFocus(target.scrollHeight > target.clientHeight);
			}),
		);

		const setLazyKeylines = useLazyCallback(
			rafSchedule(() => {
				const target = scrollableRef.current;
				if (target) {
					const scrollableDistance = target.scrollHeight - target.clientHeight;

					if (hasSiblings.current.previous) {
						setTopKeyline(target.scrollTop > keylineHeight);
					}

					if (hasSiblings.current.next) {
						setBottomKeyline(target.scrollTop <= scrollableDistance - keylineHeight);
					}
				}
			}),
		);

		useEffect(() => {
			const target = scrollableRef.current;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('resize', setLazyKeylines, false);
			target?.addEventListener('scroll', setLazyKeylines, false);

			setLazyContentFocus();
			setLazyKeylines();
			setLazySiblings({
				previous: Boolean(target?.previousElementSibling),
				next: Boolean(target?.nextElementSibling),
			});

			return () => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.removeEventListener('resize', setLazyKeylines, false);
				target?.removeEventListener('scroll', setLazyKeylines, false);
			};
		}, [setLazyContentFocus, setLazyKeylines, setLazySiblings]);
		return (
			<FocusRing isInset>
				<ScrollContainerWrapper
					tabIndex={showContentFocus ? 0 : undefined}
					data-testid={testId !== null && testId !== undefined && `${testId}--scrollable`}
					ref={mergeRefs([ref, scrollableRef])}
					showTopKeyline={!disableAutoTopKeyline && showTopKeyline}
					showBottomKeyline={!disableAutoBottomKeyline && showBottomKeyline}
				>
					{children}
				</ScrollContainerWrapper>
			</FocusRing>
		);
	},
);

ScrollContainer.displayName = 'ScrollContainer';

export default ScrollContainer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScrollContainerWrapper = styled.div<{
	showTopKeyline: boolean;
	showBottomKeyline: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderTop: ({ showTopKeyline }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`${showTopKeyline ? `${keylineHeight}px solid ${keylineColor}` : 'none'}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderBottom: ({ showBottomKeyline }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`${showBottomKeyline ? `${keylineHeight}px solid ${keylineColor}` : 'none'}`,
	display: 'inherit',
	margin: 0,
	flex: 'inherit',
	flexDirection: 'inherit',
	overflowX: 'hidden',
	overflowY: 'auto',
	'@media (min-width: 480px)': {
		height: 'unset',
		overflowY: 'auto',
	},
});
